<script lang="ts">
  import { auth } from '@/stores/user';
  import { page } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import DarkModeToggle from '@/components/ui/DarkModeToggle.svelte';
  import { notifications, unreadCount } from '@/stores/notifications';

  let {
    ontoggleSidebar
  }: {
    ontoggleSidebar?: () => void;
  } = $props();

  let profileOpen = $state(false);

  // 🔴 하드코딩된 `3` 을 걷어냈다. 읽든 안 읽든 늘 3 이 떠 있어서, 배지가
  //    "알림이 있다" 가 아니라 아무 의미도 없는 장식이었다.
  //
  //    값은 서버가 Inertia shared props 로 준다(`shared_unread_notifications_count`).
  //    여기서 API 를 부르지 않는 이유는 그 주석에 적어 뒀다 — 배지는 전 화면에
  //    떠 있어서 페이지마다 요청이 하나씩 늘고, dev 자동로그인에서는 401 이라
  //    배지가 조용히 사라진다. 읽음 처리 후 감소는 스토어가 이어받는다.
  onMount(() => {
    const initial = (page.props as any)?.notifications?.unread_count;
    if (typeof initial === 'number') notifications.updateCount(initial);
  });
  let devAutoLoggedIn = $derived(Boolean((page.props as any)?.auth?.dev_auto_logged_in));
  let loginPath = $derived(((page.props as any)?.auth?.login_path as string) || '/login');

  function handleLogout() {
    // 🔴 dev 자동로그인도 이제 **실제 세션**을 갖는다. 예전처럼 로그인 화면으로
    //    보내기만 하면 세션이 그대로 살아 있어 다음 페이지에서 다시 로그인된
    //    상태가 된다 — 로그아웃을 눌렀는데 로그아웃이 안 되는 셈이다.
    //    아래 공통 경로가 DELETE /logout 으로 세션을 지우고, `loginPath` 는
    //    서버가 이미 `?force=1` 로 내려준다(자동 로그인 재진입 방지).
    auth.clearUser();
    fetch('/logout', {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      redirect: 'manual',
    }).finally(() => {
      window.location.href = loginPath;
    });
  }

  function closeProfileMenu() {
    profileOpen = false;
  }

  let organizationLabel = $derived(
    (($auth.user as any)?.organization || ($auth.user as any)?.organization_name || '').trim()
  );

  $effect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && profileOpen) {
        profileOpen = false;
      }
    }
    function handleClick() {
      closeProfileMenu();
    }
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClick);
    };
  });
</script>

<nav class="sticky top-0 z-30 border-b border-default/60 bg-white/95 dark:bg-dark-secondary/95 backdrop-blur-xl shadow-sm">
  <div class="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex items-center justify-between h-16">
      <!-- Left side - Menu button only -->
      <div class="flex items-center gap-4">
        <button
          onclick={() => ontoggleSidebar?.()}
          class="p-2 -ml-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
          aria-label="메뉴 열기"
        >
          <svg
            class="h-6 w-6 text-content-secondary group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      <!-- Right side - Actions and user menu -->
      <div class="flex items-center gap-2">
        <!-- Favorite spaces shortcut -->
        <a
          href="/boards?favorites=1#favorite-spaces"
          class="relative p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
          aria-label="즐겨찾기 스페이스"
          title="즐겨찾기 스페이스"
        >
          <svg class="w-5 h-5 text-warning group-hover:text-warning transition-colors duration-200" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </a>

        <!-- Notifications -->
        <a
          href="/notifications"
          class="relative p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
          aria-label="알림"
        >
          <svg class="w-5 h-5 text-content-secondary group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
          {#if $unreadCount > 0}
            <span class="absolute top-1 right-1 w-5 h-5 bg-error text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md">
              {$unreadCount > 9 ? '9+' : $unreadCount}
            </span>
          {/if}
        </a>

        <!-- Messages -->
        <a
          href="/messages"
          class="relative p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group hidden sm:block"
          aria-label="메시지"
        >
          <svg class="w-5 h-5 text-content-secondary group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
        </a>

        <!-- Divider -->
        <div class="w-px h-6 bg-gray-200 dark:bg-dark-border mx-1"></div>

        <!-- Dark Mode Toggle -->
        <DarkModeToggle />

        <!-- Divider -->
        <div class="w-px h-6 bg-gray-200 dark:bg-dark-border mx-1"></div>

        <!-- User profile dropdown -->
        <div class="relative">
          <button
            onclick={(e) => { e.stopPropagation(); profileOpen = !profileOpen; }}
            class="flex items-center gap-3 pl-2 pr-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
            aria-expanded={profileOpen}
            aria-haspopup="true"
            aria-label="프로필 메뉴"
          >
            <!-- Avatar with status -->
            <div class="relative">
              <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-bold shadow-md group-hover:scale-105 transition-transform duration-200">
                {$auth.user?.name?.[0] || '?'}
              </div>
              <!-- Online status indicator -->
              <div class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-white dark:border-dark-secondary shadow-sm"></div>
            </div>

            <!-- User info (desktop only) -->
            <div class="hidden lg:block text-left">
              <p class="text-sm font-semibold text-content leading-tight truncate max-w-[140px]">
                {$auth.user?.name || $auth.user?.nickname || $auth.user?.email?.split('@')[0] || '사용자'}
              </p>
              <p class="text-xs text-content-tertiary leading-tight truncate max-w-[140px]">
                {organizationLabel || '조직 없음'}
              </p>
            </div>

            <!-- Chevron icon -->
            <svg
              class="w-4 h-4 text-content-tertiary hidden lg:block shrink-0 transition-transform duration-200 {profileOpen ? 'rotate-180' : ''}"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Dropdown menu -->
          {#if profileOpen}
            <div
              class="absolute right-0 mt-2 w-72 bg-surface rounded-2xl shadow-2xl border border-default p-2 animate-scale-in origin-top-right z-50"
              role="menu"
              aria-orientation="vertical"
            >
              <!-- User info header -->
              <div class="px-4 py-3 mb-2 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary/10 dark:to-primary/5 rounded-xl">
                <div class="flex items-center gap-3 mb-2">
                  <div class="relative">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                      {$auth.user?.name?.[0] || '?'}
                    </div>
                    <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-white dark:border-dark-secondary shadow-sm"></div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-bold text-content truncate">
                      {$auth.user?.name || $auth.user?.nickname || $auth.user?.email?.split('@')[0] || '사용자'}
                    </p>
                    <p class="text-xs text-content-secondary truncate">
                      {$auth.user?.email || ''}
                    </p>
                  </div>
                </div>
                {#if devAutoLoggedIn}
                  <div class="mb-2 inline-flex items-center gap-1.5 rounded-full bg-warning-light px-2.5 py-1 text-[11px] font-semibold text-warning-dark dark:bg-warning/15 dark:text-warning">
                    <span class="inline-block h-1.5 w-1.5 rounded-full bg-warning"></span>
                    개발 자동 로그인
                  </div>
                {/if}
                {#if organizationLabel}
                  <div class="flex items-center gap-2 px-3 py-1.5 bg-white/80 dark:bg-dark-secondary/50 rounded-lg">
                    <svg class="w-3.5 h-3.5 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                    <span class="text-xs font-medium text-gray-700 dark:text-dark-text-medium truncate">
                      {organizationLabel}
                    </span>
                  </div>
                {/if}
              </div>

              <!-- Menu items -->
              <div class="space-y-1">
                <a
                  href="/dashboard"
                  class="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-dark-text-medium hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
                  role="menuitem"
                >
                  <div class="w-9 h-9 bg-primary-50 dark:bg-primary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <svg class="w-4.5 h-4.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                    </svg>
                  </div>
                  <span class="group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200">마이페이지</span>
                </a>

                <a
                  href="/settings"
                  class="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-dark-text-medium hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer group"
                  role="menuitem"
                >
                  <div class="w-9 h-9 bg-surface-tertiary rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <svg class="w-4.5 h-4.5 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <span class="group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200">설정</span>
                </a>

                <!-- TODO: 도움말 페이지 구현 후 복원 -->
              </div>

              <!-- Divider -->
              <div class="my-2 border-t border-default"></div>

              <!-- Logout button -->
              <button
                onclick={handleLogout}
                class="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl text-sm font-medium text-error hover:bg-error-light dark:hover:bg-error/15 transition-all duration-200 cursor-pointer group"
                role="menuitem"
              >
                <div class="w-9 h-9 bg-error-light dark:bg-error/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <svg class="w-4.5 h-4.5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                </div>
                <span class="group-hover:font-bold transition-all duration-200">
                  <!-- dev 자동로그인도 이제 실제 세션을 지우므로 동작이 같다.
                       "로그인 화면 보기" 는 세션이 남던 시절의 문구였다. -->
                  로그아웃
                </span>
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
</nav>

<style>
  /* Ensure smooth transitions respect reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
